import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { useAuth } from '../auth/authContext'
import { parseChecks } from '../lib/checks'
import {
  beitraege,
  beitraegeFuerExport,
  darfBearbeiten,
  neuerPost,
  ueberfuehre,
  type Verfasser,
} from '../lib/diary'
import { newId } from '../lib/id'
import { blobToDataUrl, dataUrlToBlob, prepareImage } from '../lib/image'
import {
  EXPORT_FORMAT,
  EXPORT_VERSION,
  type Bundle,
  type ExportFile,
  type TransferPhoto,
} from '../lib/transfer'
import {
  EMPTY_TRIP,
  insertStageAfter,
  migrateExpense,
  moveStage,
  removeStage,
  renumber,
  type Reisestart,
} from '../lib/trip'
import { fahrzeug, mitFahrzeug, ueberfuehreFahrzeug } from '../lib/vehicle'
import {
  KIND,
  SINGLETON_ID,
  type Checks,
  type DiaryEntry,
  type DiaryPost,
  type Expense,
  type PhotoMeta,
  type Stage,
  type Trip,
  type TripMeta,
  type Vehicle,
} from '../lib/types'
import {
  ALTBESTAND,
  allDocs,
  deleteBlob,
  deletePhotoDoc,
  getBlob,
  putBlob,
  putDocs,
  putLocalChange,
  tombstonesForImport,
  wipe,
  wipeAlles,
  type LocalDoc,
} from './db'
import { mergeSnapshot } from './merge'
import { RoadbookContext, type RoadbookValue } from './roadbookContext'
import { statusNachStilllegung, SyncEngine, type SyncStatus } from './sync'
import { useTrip } from './tripContext'
import { altbestandAnzeige, altbestandVerfasser } from './trips'

/**
 * Wartezeit zwischen der letzten Eingabe und dem Abgleich. Ohne sie löst jeder
 * Tastendruck im Tagebuch einen Netzaufruf aus.
 */
const SYNC_DEBOUNCE_MS = 2500

type DocMap = Map<string, LocalDoc>

function docKey(kind: string, id: string): string {
  return `${kind}:${id}`
}

function toMap(docs: LocalDoc[]): DocMap {
  return new Map(docs.map((d) => [docKey(d.kind, d.id), d]))
}

/**
 * Rückt die Folgezeilen eines JSON-Abschnitts um eine Stufe ein.
 *
 * Die Exportdatei entsteht abschnittsweise, jeder Abschnitt für sich durch
 * JSON.stringify. Ohne das hier stünde jeder Abschnitt am linken Rand, und die
 * Datei wäre nicht mehr das lesbare Sicherungsband, das sie war, als sie noch in
 * einem Stück entstand.
 */
function eingerueckt(json: string): string {
  return json.replaceAll('\n', '\n  ')
}

/**
 * Der Bestand einer Reise.
 *
 * Die Reise steht fest, solange dieser Provider steht: sie kommt aus dem
 * Reisekontext, und der Wechsel baut ihn über den key in App.tsx vollständig
 * neu auf. Das ist Absicht und die einfachste Form der Trennung - kein
 * Zustand, keine Referenz und kein laufender Abgleich der alten Reise überlebt
 * den Wechsel, weil es die Instanz nicht mehr gibt, an der sie hingen.
 */
export function RoadbookProvider({ children }: { children: ReactNode }) {
  const { status: authStatus, userId, displayName } = useAuth()
  const { aktiv, darfSchreiben, besitzer, meineEmail, zugriffVerloren, vergissAlles, api } =
    useTrip()
  const tripId = aktiv?.id ?? ALTBESTAND

  const [docs, setDocs] = useState<DocMap>(() => new Map())
  const [ready, setReady] = useState(false)
  const [sync, setSync] = useState<SyncStatus>({
    state: 'idle',
    lastSync: null,
    pending: 0,
    failedUploads: 0,
  })

  const docsRef = useRef<DocMap>(docs)
  docsRef.current = docs

  const engineRef = useRef<SyncEngine | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const seqRef = useRef(0)

  /**
   * Wahr, sobald wipeForSignOut den Bestand stillgelegt hat.
   *
   * Ab da darf nichts mehr in die Datenbank. Das Vorbereiten eines Fotos dauert
   * ein bis zwei Sekunden; fällt das Abmelden in dieses Fenster, landete das
   * Bild sonst in der frisch geleerten Datenbank und der Nächste am Gerät
   * sähe es. Ein Ref und keine State-Variable: die Prüfung muss im selben Tick
   * greifen, in dem das Abmelden lief, nicht erst nach dem nächsten Rendern.
   *
   * Zurückgesetzt wird die Markierung nicht. Das ist Absicht und nicht
   * Vergesslichkeit: solange das Token noch gilt - und genau dann bleibt der
   * Provider überhaupt stehen, nämlich wenn die Abmeldung beim Anmeldedienst
   * scheitert -, hiesse Weiterschreiben auch Weiterabgleichen, und der nächste
   * Pull holte den Bestand des abgemeldeten Nutzers zurück auf dieses Gerät.
   * Sichtbar ist der Zustand trotzdem: wipeForSignOut setzt die Statusleiste auf
   * "Abgemeldet", siehe statusNachStilllegung in sync.ts.
   */
  const stillgelegtRef = useRef(false)

  /**
   * Die nächste lokale Änderungsnummer.
   *
   * Sie muss grösser sein als alles bisher Vergebene, nicht nur grösser als
   * die des Vorgängers: zwei Schreibvorgänge im selben Tick sehen dieselbe
   * Vorgängerfassung, denn der React-Zustand ist bis zum nächsten Rendern
   * derselbe. Bekämen sie dieselbe Nummer, hälte der Push die zweite Fassung
   * fälschlich für gesendet und der Server behielte still den alten Rumpf.
   */
  const nextSeq = useCallback((previous?: number) => {
    seqRef.current = Math.max(seqRef.current, previous ?? 0) + 1
    return seqRef.current
  }, [])

  /**
   * Übernimmt den Bestand aus der lokalen Datenbank in den React-Zustand.
   *
   * Die Entscheidung, was dabei Vorrang hat, steckt in mergeSnapshot: rein,
   * exportiert und geprüft. Sie ist der heikelste Teil dieser Datei, und hier
   * im Provider läge sie ungetestet.
   */
  const reload = useCallback(async () => {
    let snapshot: DocMap
    try {
      snapshot = toMap(await allDocs(tripId))
    } catch (err) {
      // Die Datenbank ist weg: abgemeldet, gelöscht, oder der Browser gibt im
      // privaten Modus keine her. Gerufen wird das ohne await, eine Ablehnung
      // landete also als unbehandelte Ablehnung in der Konsole.
      console.error('Lokaler Bestand liess sich nicht lesen', err)
      return
    }
    // Nach dem Abmelden ist der Bestand weg und soll es bleiben. Ein Abgleich,
    // der noch in der Luft war, darf ihn nicht wieder in die Oberfläche holen.
    if (stillgelegtRef.current) return
    setDocs((current) => mergeSnapshot(current, snapshot))
  }, [tripId])

  /**
   * Erststart: den lokalen Bestand laden.
   *
   * Angelegt wird hier nichts mehr. Bis hierher legte diese Stelle die
   * vorgeplante Balkanroute in jede Reise, in der kein Reisedokument lag - für
   * die eine Reise, für die die App gebaut wurde, war das richtig und für jede
   * zweite falsch. Wer heute eine Reise anlegt, entscheidet dort, ob er die
   * Vorlage will; sie wird dann vor dem Öffnen geschrieben (data/beispiel.ts).
   *
   * Eine Reise ohne Reisedokument ist also eine leere Reise, und die zeigt die
   * App auch als solche - siehe EMPTY_TRIP weiter unten. Das gilt auch für
   * den Fall, für den die Startbestückung nie gedacht war und den sie trotzdem
   * traf: ein zweites Gerät, auf dem die Reise zum ersten Mal aufgeht. Dort
   * stand die Vorlage bisher so lange, bis der erste Pull durch war.
   */
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const stored = await allDocs(tripId)
      if (cancelled) return

      setDocs(toMap(stored))
      setReady(true)
    })().catch((err: unknown) => {
      // Ohne lesbare Datenbank bleibt ready falsch, und die App zeigt weiter
      // "Lokaler Bestand wird gelesen". Das ist die ehrlichere Anzeige als eine
      // leere Reise, die aussieht, als wäre nichts da.
      console.error('Lokaler Bestand liess sich nicht öffnen', err)
    })
    return () => {
      cancelled = true
    }
  }, [tripId])

  // Der Abgleich läuft nur, solange eine Anmeldung besteht. Im Offline-Modus
  // arbeitet die App weiter, schiebt aber nichts zum Server.
  //
  // Und er läuft nur mit einer Reise. Ein Bestand, der noch keiner zugeordnet
  // ist, wird nicht abgeglichen: wohin, weiss niemand, und ihn auf gut Glück
  // irgendwohin zu schieben wäre die eine Vermischung, die sich nicht mehr
  // auflösen liesse. Die Zuordnung läuft im TripProvider, sobald Netz da ist.
  useEffect(() => {
    if (!ready || authStatus !== 'ready') return
    if (tripId === ALTBESTAND) return

    const engine = new SyncEngine(api, tripId, {
      onDataChanged: () => void reload(),
      onZugriffWeg: zugriffVerloren,
      darfSchreiben,
    })
    engineRef.current = engine
    const unsubscribe = engine.subscribe(setSync)
    void engine.start()

    return () => {
      unsubscribe()
      engine.stop()
      engineRef.current = null
    }
  }, [ready, authStatus, api, tripId, darfSchreiben, reload, zugriffVerloren])

  useEffect(() => {
    if (authStatus === 'offline') {
      setSync((s) => ({ ...s, state: 'offline' }))
    }
  }, [authStatus])

  const scheduleSync = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      void engineRef.current?.sync()
    }, SYNC_DEBOUNCE_MS)
  }, [])

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    },
    [],
  )

  /**
   * Schreibt ein Dokument: erst lokal, dann in den Zustand, dann irgendwann zum
   * Server. Diese Reihenfolge ist der Kern des Offline-Betriebs.
   *
   * Die Basisrevision kommt aus dem React-Zustand, und der kann in der Revision
   * hinterherhinken - im schlimmsten Fall steht dort noch die Vorlage mit rev 0,
   * während der Abgleich die echte Fassung längst in die Datenbank geschrieben
   * hat. Deshalb entscheidet putLocalChange in einer Transaktion, welche
   * Revision tatsächlich gespeichert wird, und der Zustand zieht danach nach.
   *
   * @param options.at setzt den Zeitstempel der Änderung, statt die Uhr zu
   *   fragen. Gebraucht wird das an genau einer Stelle: bei der Überführung
   *   des alten Tagebuchtexts in einen Beitrag. Diese Änderung ist keine
   *   Eingabe von jetzt, sondern die Wiedergabe von etwas, das längst
   *   geschrieben wurde - und wenn ein Gerät, das lange offline war, sie mit
   *   dem heutigen Zeitstempel nachholt, gewinnt sie im Abgleich gegen einen
   *   Beitrag, den inzwischen jemand geändert oder gelöscht hat. Übergeben wird
   *   dort die 0, siehe ALTBESTAND_DOKUMENTZEIT in lib/diary.ts.
   */
  const write = useCallback(
    (
      kind: string,
      id: string,
      body: unknown,
      options: { deleted?: boolean; push?: boolean; at?: number } = {},
    ) => {
      const key = docKey(kind, id)
      const previous = docsRef.current.get(key)
      const doc: LocalDoc = {
        kind,
        id,
        body,
        rev: previous?.rev ?? 0,
        deleted: options.deleted ?? false,
        dirty: options.push === false ? 0 : 1,
        updatedAt: options.at ?? Date.now(),
        // seed wird bewusst nicht übernommen: sobald der Nutzer die Vorlage
        // anfasst, ist es sein Inhalt und darf im Abgleich nicht mehr
        // bedingungslos verlieren.
        localSeq: nextSeq(previous?.localSeq),
      }

      // Nach dem Abmelden ist die Datenbank geleert. Was jetzt noch geschrieben
      // würde, gehörte niemandem mehr und bliebe für den Nächsten liegen.
      // Stumm verworfen wird es deshalb trotzdem nicht: die Statusleiste steht
      // seit dem Stilllegen auf "Abgemeldet", und in die Konsole gehört es
      // auch - wer hier landet, sucht sonst lange.
      if (stillgelegtRef.current) {
        console.warn('Nach dem Abmelden nichts mehr gespeichert', kind, id)
        return doc
      }

      // Nur lesende Rolle. Die Oberfläche zeigt gar keine Schreibfelder mehr,
      // aber die Regel gehört hierher und nicht in die Knöpfe: eine Ansicht,
      // die jemand übersehen hat, schriebe sonst in einen Bestand, den der
      // Server nie annimmt - und der Nutzer sähe seine Eingabe stehen, bis er
      // die App das nächste Mal öffnet.
      if (!darfSchreiben) {
        console.warn('Nur Leserecht in dieser Reise, nichts gespeichert', kind, id)
        return doc
      }

      setDocs((current) => new Map(current).set(key, doc))
      void (async () => {
        const gespeichert = await putLocalChange(tripId, doc)
        if (gespeichert.rev === doc.rev) return
        setDocs((current) => {
          // Nur die eigene Fassung nachziehen. Ein späterer write() trägt eine
          // höhere Änderungsnummer und darf nicht zurückgedreht werden.
          if (current.get(key)?.localSeq !== doc.localSeq) return current
          return new Map(current).set(key, gespeichert)
        })
      })()
      if (doc.dirty === 1) scheduleSync()
      return doc
    },
    [tripId, darfSchreiben, nextSeq, scheduleSync],
  )

  // --- Abgeleitete Sicht auf den Bestand -----------------------------------

  /**
   * Die Reise, oder eine leere, wenn kein Dokument da ist.
   *
   * Hier stand SEED_TRIP, und das war die dritte Stelle, an der eine fremde
   * Reise auftauchte, ohne dass jemand sie angelegt hätte: bei einer Reise,
   * deren Dokument noch nicht angekommen ist - ein zweites Gerät vor dem
   * ersten Pull, eine Rolle mit Nur-Leserecht - stand hier die Balkanroute
   * samt Titel im Kopf der App. Leer ist die ehrliche Auskunft.
   */
  const trip = useMemo<Trip>(() => {
    const doc = docs.get(docKey(KIND.trip, SINGLETON_ID.trip))
    return doc && !doc.deleted ? (doc.body as Trip) : EMPTY_TRIP
  }, [docs])

  /**
   * Die Checklisten aus dem Dokument.
   *
   * Gelesen wird über parseChecks, weil hier beide Formate ankommen können: auf
   * einer laufenden Installation liegt das alte, in dem nur Haken je Name
   * standen, und über den Abgleich kann es jederzeit wieder hereinkommen - von
   * einem Gerät, das lange nicht online war. Die Überführung passiert beim
   * Lesen und wird nicht von selbst zurückgeschrieben: sie füllt die Einträge
   * aus der Vorlage auf, denn das alte Format speicherte nur Haken je Name.
   * Das ist keine Änderung, die die App ungefragt in das Dokument des Nutzers
   * schreiben sollte; der nächste Handgriff in der Checkliste speichert
   * ohnehin das neue Format.
   *
   * Fehlt das Dokument ganz, kommen zwei leere Listen zurück - und nicht mehr
   * die Vignetten von Slowenien bis Bulgarien.
   */
  const checks = useMemo<Checks>(() => {
    const doc = docs.get(docKey(KIND.checks, SINGLETON_ID.checks))
    return parseChecks(doc && !doc.deleted ? doc.body : undefined)
  }, [docs])

  const diary = useMemo<Record<string, DiaryEntry>>(() => {
    const out: Record<string, DiaryEntry> = {}
    for (const doc of docs.values()) {
      if (doc.kind !== KIND.diary || doc.deleted) continue
      out[doc.id] = doc.body as DiaryEntry
    }
    return out
  }, [docs])

  /** Wer gerade schreibt. Aus dem Token, nicht aus einem Eingabefeld. */
  const ich = useMemo<Verfasser>(() => ({ id: userId, name: displayName }), [userId, displayName])

  /**
   * Wer den Tagebuchtext aus der Zeit vor den Beiträgen geschrieben hat - oder
   * null, dann wird er nicht überführt.
   *
   * Der alte Text stammt aus der Zeit, in der eine Reise genau ein Konto hatte:
   * geschrieben hat ihn der Besitzer. Überführt wird er deshalb nur auf dessen
   * eigenem Gerät, wo Kennung und Name vollständig vorliegen. Auf jedem anderen
   * geschieht nichts - siehe altbestandVerfasser in data/trips.ts.
   */
  const altVerfasser = useMemo<Verfasser | null>(
    () => altbestandVerfasser(ich, meineEmail, besitzer),
    [ich, meineEmail, besitzer],
  )

  /**
   * Unter welchem Namen der noch nicht überführte Text angezeigt wird.
   *
   * Nicht unter dem des Anwesenden. Solange die Überführung nicht gelaufen ist,
   * steht dort die Adresse des Besitzers und eine leere Kennung: der Text
   * gehört jemandem, dessen Konto diese App nicht kennt, und ändern darf ihn
   * niemand.
   */
  const altAnzeige = useMemo<Verfasser>(
    () => altbestandAnzeige(altVerfasser, besitzer),
    [altVerfasser, besitzer],
  )

  /**
   * Die Tagebuchbeiträge je Etappe, in Leserichtung.
   *
   * Gelesen wird über beitraege(), weil hier zweierlei ankommen kann: die
   * Beitragsdokumente und der alte Tagebuchtext aus der Zeit, als ein Tag genau
   * ein Textfeld hatte. Der alte Text kommt jederzeit über den Abgleich herein
   * - von einem Gerät, das lange nicht online war -, und er wird beim Lesen zu
   * einem Beitrag, ohne dass dafür etwas geschrieben werden müsste. Der Effekt
   * weiter unten schreibt es zusätzlich einmal fest; hier ist es die Zusage,
   * dass nichts unsichtbar wird, solange das noch nicht geschehen ist.
   *
   * Die Kennungen der gelöschten Beiträge gehen mit hinein: ohne sie holte ein
   * alter Text, der später hereinschneit, einen Beitrag zurück, den jemand
   * gelöscht hat.
   */
  const postDocs = useMemo(() => {
    const nachEtappe: Record<string, DiaryPost[]> = {}
    // Auch die gelöschten: ihre Kennungen entscheiden mit, ob ein alter Text
    // noch einmal zu einem Beitrag wird.
    const bekannt = new Set<string>()

    for (const doc of docs.values()) {
      if (doc.kind !== KIND.post) continue
      bekannt.add(doc.id)
      if (doc.deleted) continue
      const post = doc.body as DiaryPost
      if (!post?.stageId) continue
      ;(nachEtappe[post.stageId] ??= []).push(post)
    }
    return { nachEtappe, bekannt }
  }, [docs])

  const postsByStage = useMemo<Record<string, DiaryPost[]>>(() => {
    const { nachEtappe, bekannt } = postDocs
    const out: Record<string, DiaryPost[]> = {}
    for (const stageId of new Set([...Object.keys(nachEtappe), ...Object.keys(diary)])) {
      const liste = beitraege(diary[stageId], nachEtappe[stageId] ?? [], altAnzeige, bekannt)
      if (liste.length > 0) out[stageId] = liste
    }
    return out
  }, [postDocs, diary, altAnzeige])

  const expenses = useMemo<Expense[]>(() => {
    const out: Expense[] = []
    for (const doc of docs.values()) {
      if (doc.kind !== KIND.expense || doc.deleted) continue
      // Umbenannte Kategorien schon hier auf den heutigen Namen bringen. Der
      // Effekt weiter unten schreibt das auch in den Bestand zurück, aber die
      // Anzeige soll nicht eine Runde lang den alten Namen zeigen - und
      // abgemeldet oder mitten im Import wird nichts geschrieben.
      //
      // Entschieden wird das an der Reise und nicht an ihrer Kraftstoffart:
      // umbenannt wird nur in einem Bestand aus der Zeit vor dem Fahrzeug.
      // Fehlt das Reisedokument, steht hier die leere Reise - und die trägt ihr
      // Fahrzeug, ist also kein Altbestand. Siehe currentCategory in lib/trip.ts.
      out.push(migrateExpense(doc.body as Expense, trip))
    }
    return out.sort((a, b) =>
      a.date === b.date ? a.id.localeCompare(b.id) : a.date < b.date ? -1 : 1,
    )
  }, [docs, trip])

  const photosByStage = useMemo<Record<string, PhotoMeta[]>>(() => {
    const out: Record<string, PhotoMeta[]> = {}
    for (const doc of docs.values()) {
      if (doc.kind !== KIND.photo || doc.deleted) continue
      const meta = doc.body as PhotoMeta
      if (!meta?.stageId) continue
      ;(out[meta.stageId] ??= []).push(meta)
    }
    for (const list of Object.values(out)) list.sort((a, b) => a.createdAt - b.createdAt)
    return out
  }, [docs])

  /**
   * Überführt den Tagebuchtext aus der Zeit vor den Beiträgen einmal in einen
   * Beitrag.
   *
   * Derselbe Bau wie bei der Überführung des Fahrzeugs weiter unten, und aus
   * denselben Gründen: der Effekt hängt am gesamten Bestand und nicht nur am
   * ersten Laden, denn ein alter Text kann auch Wochen später noch über den
   * Abgleich hereinkommen. Eine Schleife ist das nicht - nach dem Schreiben ist
   * DiaryEntry.text leer, und der nächste Durchgang findet nichts mehr.
   *
   * Zweimal ausgeführt entstehen keine zwei Beiträge. Dafür sorgen zwei Dinge:
   * die Kennung des Beitrags wird aus der Etappen-ID gerechnet (altbestandId),
   * zwei Geräte erzeugen also dasselbe Dokument statt zweier - und ein Beitrag,
   * den es hier schon gibt, wird nicht noch einmal geschrieben. Das Zweite
   * gilt ausdrücklich auch für einen Grabstein: wer den überführten Beitrag
   * gelöscht hat, bekommt ihn nicht dadurch zurück, dass irgendwo noch ein
   * Gerät mit dem alten Text steht.
   *
   * Überführt wird nur auf dem Gerät des Besitzers der Reise, und nur wenn die
   * Mitgliederliste vorliegt (altVerfasser). Sonst geschieht nichts: der alte
   * Text bleibt sichtbar - postsByStage liest ihn weiterhin mit - und wartet.
   * Das ist der Riegel gegen die Falle, die diese Stelle vorher war: der Text
   * wurde dem zugeschrieben, der die App gerade offen hatte, und sobald eine
   * Reise geteilt wird, ist das der Falsche.
   */
  useEffect(() => {
    if (!ready || !darfSchreiben) return
    for (const doc of docs.values()) {
      if (doc.kind !== KIND.diary || doc.deleted) continue
      const auftrag = ueberfuehre(doc.body as DiaryEntry, altVerfasser, postDocs.bekannt)
      // Der Zeitstempel des Dokuments kommt aus dem Auftrag und nicht aus dem
      // Rumpf des Beitrags: eine Überführung ist keine Änderung von jetzt und
      // darf im Abgleich keinen Konflikt gewinnen - auch nicht gegen einen
      // Grabstein, und auch nicht bei einer Reise, deren Etappendaten noch in
      // der Zukunft liegen. Siehe ALTBESTAND_DOKUMENTZEIT in lib/diary.ts.
      if (auftrag.post) {
        write(KIND.post, auftrag.post.id, auftrag.post, { at: auftrag.zeit })
      }
      if (auftrag.entry) write(KIND.diary, doc.id, auftrag.entry)
    }
  }, [ready, darfSchreiben, docs, postDocs, write, altVerfasser])

  // --- Änderungen ----------------------------------------------------------

  /**
   * Die zuletzt geschriebene Fassung der Reise.
   *
   * Die Änderungen unten setzen auf der vorigen Fassung auf, und die darf nicht
   * aus dem Abschluss des letzten Renderns kommen. Die Streckenberechnung
   * schreibt sechzehn Etappen nacheinander: käme die Grundlage aus dem
   * Renderzustand, sähe jeder dieser Schreibvorgänge dieselbe alte Fassung und
   * überschriebe die vorigen - am Ende stünde genau eine Strecke im Dokument.
   *
   * Nachgezogen wird an zwei Stellen: beim Rendern, wie beim docsRef darüber,
   * und in writeTrip selbst. Das zweite ist das Entscheidende, denn nur so ist
   * der Wert auch innerhalb eines Ticks aktuell, in dem noch nicht gerendert
   * wurde.
   */
  const tripRef = useRef(trip)
  tripRef.current = trip

  const writeTrip = useCallback(
    (next: Trip) => {
      tripRef.current = next
      return write(KIND.trip, SINGLETON_ID.trip, next)
    },
    [write],
  )

  const setMeta = useCallback(
    (patch: Partial<TripMeta>) => {
      const current = tripRef.current
      writeTrip({ ...current, meta: { ...current.meta, ...patch } })
    },
    [writeTrip],
  )

  /**
   * Ändert das Fahrzeug der Reise.
   *
   * Über mitFahrzeug und nicht als Teilstück am Dokument: Name und Verbrauch
   * gehen zusätzlich in die alten Felder, damit eine ältere Fassung der App auf
   * einem zweiten Gerät weiter etwas anzuzeigen und zu rechnen hat. Das ist die
   * einzige Stelle, die diese Kopie schreibt.
   */
  const setVehicle = useCallback(
    (patch: Partial<Vehicle>) => {
      const current = tripRef.current
      writeTrip(mitFahrzeug(current, { ...fahrzeug(current), ...patch }))
    },
    [writeTrip],
  )

  /**
   * Überführt eine Reise aus der Zeit vor dem Fahrzeug - genau einmal.
   *
   * Überführt wird zweierlei, und beides gehört zusammen: das Fahrzeug selbst
   * und die Ausgabenkategorien, die aus derselben Zeit stammen. Der alte Name
   * „Diesel“ ist die Annahme über den T3, die längst korrigiert ist (siehe
   * CATEGORY_RENAMES in lib/seed.ts), und er steht als Text in jeder Ausgabe:
   * solange er dort steht, müsste jede Auswertung ihn kennen - die Farbtabelle,
   * das Diagramm, ein Export, der Server.
   *
   * Dieselbe Bauart wie bei der Überführung des alten Tagebuchtexts: der Effekt
   * hängt am gesamten Bestand und nicht nur am ersten Laden, denn ein
   * Reisedokument ohne Fahrzeug kann jederzeit über den Abgleich hereinkommen -
   * von einem Gerät, das lange nicht online war oder noch die vorige Fassung der
   * App hält. Eine Schleife ist das nicht: nach dem Schreiben hat die Reise ein
   * Fahrzeug, und der nächste Durchgang findet nichts mehr (ueberfuehreFahrzeug
   * gibt dann null zurück).
   *
   * Was dabei entsteht, ist nichts Neues, sondern das, womit die Reise ohnehin
   * gerechnet hat: ihr Name, ihr Verbrauch, ein Benziner, 25 Prozent Zuschlag,
   * das Schild CH. Zwei Geräte, die gleichzeitig überführen, schreiben deshalb
   * dasselbe.
   *
   * Angesehen wird das Dokument und nicht die Reise aus dem Zustand: ohne
   * Dokument steht dort die leere Reise (EMPTY_TRIP), und die hat ihr Fahrzeug
   * schon - überführt würde also eine Reise, die es gar nicht gibt, und mit ihr
   * bekäme jede „Diesel“-Buchung den Namen „Benzin“, nur weil das Reisedokument
   * noch nicht angekommen ist.
   *
   * Dass die Kategorien im selben Durchgang umgeschrieben werden wie das
   * Fahrzeug entsteht, ist die Zusage, dass sie danach nie wieder umgeschrieben
   * werden: ein Fahrzeug, das später von Diesel auf Benzin gestellt wird, ändert
   * an einer erfassten Buchung nichts mehr. Bezahlt wird das mit einer Grenze,
   * und die ist ehrlich zu nennen: eine Ausgabe mit altem Namen, die erst nach
   * der Überführung hereinkommt - eine spätere Seite des ersten Abgleichs -,
   * behält ihn. Das ist die harmlose Richtung. Sie steht dann als eigene
   * Kategorie im Diagramm, während die andere Richtung eine Buchung
   * unwiederbringlich umbenennt, die jemand bewusst so genannt hat.
   */
  useEffect(() => {
    // Ohne Schreibrecht wird nichts überführt. Die Anzeige zeigt trotzdem den
    // heutigen Kategorienamen: migrateExpense läuft beim Lesen mit.
    if (!ready || !darfSchreiben) return
    const doc = docs.get(docKey(KIND.trip, SINGLETON_ID.trip))
    if (!doc || doc.deleted) return
    const neu = ueberfuehreFahrzeug(doc.body as Trip)
    if (!neu) return

    // Erst die Ausgaben, dann die Reise: das Umschreiben fragt currentCategory,
    // und die Antwort hängt genau an dem Fahrzeugfeld, das gleich entsteht.
    const alteReise = doc.body as Trip
    for (const eintrag of docs.values()) {
      if (eintrag.kind !== KIND.expense || eintrag.deleted) continue
      const alt = eintrag.body as Expense
      const umbenannt = migrateExpense(alt, alteReise)
      // Wo nichts zu tun ist, wird nicht geschrieben - migrateExpense gibt
      // dasselbe Objekt zurück.
      if (umbenannt === alt) continue
      write(KIND.expense, eintrag.id, umbenannt)
    }
    writeTrip(neu)
  }, [ready, darfSchreiben, docs, write, writeTrip])

  const setStage = useCallback(
    (id: string, patch: Partial<Stage>) => {
      const current = tripRef.current
      writeTrip({
        ...current,
        stages: current.stages.map((s) => (s.id === id ? { ...s, ...patch } : s)),
      })
    },
    [writeTrip],
  )

  /**
   * Legt eine Etappe an und gibt ihre Kennung zurück - oder eine leere
   * Zeichenkette, wenn keine entstanden ist.
   *
   * Das ist der Fall, wenn die Stelle nicht feststeht: die erste Etappe einer
   * Reise, ohne dass jemand gesagt hätte, wo sie beginnt - oder eine Etappe
   * hinter einer Kennung, die es in dieser Reise nicht gibt. Erfunden wird dann
   * nichts (siehe insertStageAfter).
   *
   * Der zweite Fall ist nicht bloss ein übersprungener Schritt in der
   * Oberfläche: gearbeitet wird auf tripRef.current, und der Abgleich kann
   * zwischen dem Aufbau der Maske und dem Druck auf den Knopf Etappen von einem
   * anderen Gerät gebracht haben. Wer den leeren Rückgabewert übergeht, hängt
   * seine Etappe stillschweigend woandershin - deshalb wertet StagesView ihn
   * aus und sagt es.
   */
  const addStage = useCallback(
    (afterId: string, start?: Reisestart) => {
      const current = tripRef.current
      const eingefuegt = insertStageAfter(current.stages, afterId, start)
      if (!eingefuegt) {
        console.warn('Ohne vorherige Etappe und ohne Start wird keine Etappe angelegt')
        return ''
      }
      writeTrip({ ...current, stages: eingefuegt.stages })
      return eingefuegt.created.id
    },
    [writeTrip],
  )

  /**
   * Hängt fertige Etappen ans Ende der Reise.
   *
   * Der Weg für die Etappen, die aus einer Aufzeichnung entstehen
   * (features/diary/anlegen.ts). Sie kommen als ganze Kette herein und nicht
   * einzeln, und das ist der Punkt: nacheinander angelegt wäre jede für sich
   * ein Schreibvorgang auf dem Reisedokument, und die Kette entstünde erst
   * nebenbei aus sechzehn Zwischenständen.
   *
   * Angehängt und ausdrücklich nirgends eingefügt: was jemand geplant hat,
   * bleibt, wo es ist, behält seine Reihenfolge und wird nicht angefasst. Neu
   * vergeben werden nur die Tagesnummern, und die sind die Stelle in der Liste
   * (renumber in lib/trip.ts) - ohne das trüge jede neue Etappe die 0.
   */
  const appendStages = useCallback(
    (neue: Stage[]) => {
      if (neue.length === 0) return
      const current = tripRef.current
      writeTrip({ ...current, stages: renumber([...current.stages, ...neue]) })
    },
    [writeTrip],
  )

  const deleteStage = useCallback(
    (id: string) => {
      const current = tripRef.current
      writeTrip({ ...current, stages: removeStage(current.stages, id) })
    },
    [writeTrip],
  )

  const shiftStage = useCallback(
    (id: string, direction: -1 | 1) => {
      const current = tripRef.current
      const stages = moveStage(current.stages, id, direction)
      if (stages !== current.stages) writeTrip({ ...current, stages })
    },
    [writeTrip],
  )

  const setDiary = useCallback(
    (stageId: string, patch: Partial<DiaryEntry>) => {
      const current = diary[stageId] ?? {
        stageId,
        done: false,
        date: '',
        odo: '',
        realKm: '',
        weather: '',
        text: '',
      }
      write(KIND.diary, stageId, { ...current, ...patch, stageId })
    },
    [diary, write],
  )

  /**
   * Die Beiträge, wie sie zuletzt geschrieben wurden - aus demselben Grund wie
   * tripRef: Ändern und Löschen setzen auf dem vorigen Stand auf, und der darf
   * nicht aus dem Abschluss des letzten Renderns kommen.
   */
  const postsRef = useRef(postsByStage)
  postsRef.current = postsByStage

  /** Ein Beitrag über alle Etappen hinweg, gesucht am zuletzt gelesenen Stand. */
  const findePost = useCallback((id: string): DiaryPost | undefined => {
    for (const liste of Object.values(postsRef.current)) {
      const treffer = liste.find((p) => p.id === id)
      if (treffer) return treffer
    }
    return undefined
  }, [])

  const addPost = useCallback(
    (stageId: string, text: string) => {
      const inhalt = text.trim()
      if (!inhalt) return
      const post = neuerPost(stageId, inhalt, ich)
      write(KIND.post, post.id, post)
    },
    [ich, write],
  )

  /**
   * Ändert den Text eines Beitrags.
   *
   * Nur der Verfasser darf das, und das wird hier geprüft und nicht erst in der
   * Oberfläche: die Knöpfe sind der bequeme Weg, diese Zeile ist die Regel.
   * Die Zusage gibt allerdings der Server - er stempelt die Verfasserschaft aus
   * dem geprüften Token und weist die Änderung an einem fremden Beitrag ab
   * (internal/api/posts.go). Was hier steht, verhindert, dass jemand etwas
   * tippt, das gleich darauf verworfen wird. Siehe darfBearbeiten in
   * lib/diary.ts.
   */
  const updatePost = useCallback(
    (id: string, text: string) => {
      const post = findePost(id)
      if (!post) return
      if (!darfBearbeiten(post, ich)) {
        console.warn('Beitrag gehört jemand anderem, nicht geändert', id)
        return
      }
      const inhalt = text.trim()
      // Leergeräumt heisst gelöscht. Ein Beitrag ohne Text ist eine leere Zeile
      // mit Verfassernamen darüber, im Buch wie in der App.
      if (!inhalt) {
        write(KIND.post, id, {}, { deleted: true })
        return
      }
      write(KIND.post, id, { ...post, text: inhalt, updatedAt: Date.now() } satisfies DiaryPost)
    },
    [findePost, ich, write],
  )

  const deletePost = useCallback(
    (id: string) => {
      const post = findePost(id)
      if (!post) return
      if (!darfBearbeiten(post, ich)) {
        console.warn('Beitrag gehört jemand anderem, nicht gelöscht', id)
        return
      }
      write(KIND.post, id, {}, { deleted: true })
    },
    [ich, write],
  )

  const addExpense = useCallback(
    (expense: Omit<Expense, 'id'>) => {
      const id = newId('e')
      write(KIND.expense, id, { ...expense, id })
    },
    [write],
  )

  const deleteExpense = useCallback(
    (id: string) => {
      write(KIND.expense, id, {}, { deleted: true })
    },
    [write],
  )

  /**
   * Die zuletzt geschriebene Fassung der Checklisten, aus demselben Grund wie
   * tripRef: eine Änderung setzt auf der vorigen auf, und die darf nicht aus dem
   * letzten Rendern kommen.
   */
  const checksRef = useRef(checks)
  checksRef.current = checks

  const setChecks = useCallback(
    (patch: Partial<Checks>) => {
      const next: Checks = { ...checksRef.current, ...patch }
      checksRef.current = next
      write(KIND.checks, SINGLETON_ID.checks, next)
    },
    [write],
  )

  const addPhoto = useCallback(
    async (stageId: string, file: Blob) => {
      // Das Verkleinern dauert ein bis zwei Sekunden. Fällt das Abmelden in
      // dieses Fenster, gehört das Bild zu einer Sitzung, die es nicht mehr
      // gibt: verwerfen, statt es in die geleerte Datenbank zu schreiben.
      if (!darfSchreiben) {
        console.warn('Nur Leserecht in dieser Reise, kein Foto übernommen')
        return
      }
      const prepared = await prepareImage(file)
      if (stillgelegtRef.current) return
      const id = newId('p')

      await putBlob(tripId, {
        id,
        stageId,
        blob: prepared.blob,
        uploaded: 0,
        failed: 0,
        createdAt: Date.now(),
      })
      // Zwischen Prüfung und Schreiben bleibt ein Rest Fenster offen. Landen
      // die Bytes doch noch in der geleerten Datenbank, hier wieder wegräumen.
      if (stillgelegtRef.current) {
        await deleteBlob(tripId, id)
        return
      }
      // Das Dokument entsteht endgültig beim Upload auf dem Server. Bis dahin
      // steht hier ein lokaler Platzhalter, der nicht gepusht wird.
      write(
        KIND.photo,
        id,
        {
          id,
          stageId,
          mime: prepared.blob.type,
          size: prepared.blob.size,
          sha256: '',
          createdAt: Date.now(),
        } satisfies PhotoMeta,
        { push: false },
      )
      void engineRef.current?.sync()
    },
    [tripId, darfSchreiben, write],
  )

  const deletePhoto = useCallback(
    (id: string) => {
      if (!darfSchreiben) {
        console.warn('Nur Leserecht in dieser Reise, kein Foto gelöscht', id)
        return
      }
      const key = docKey(KIND.photo, id)
      const now = Date.now()

      // Sofort aus der Anzeige nehmen, damit das Antippen wirkt. Wie das
      // Löschen aussieht, entscheidet gleich die Datenbank.
      //
      // Die Änderungsnummer gehört auch an diesen vorläufigen Eintrag: ohne
      // sie holt mergeSnapshot das Foto beim nächsten Reload aus der Datenbank
      // zurück in die Anzeige, bis der Grabstein geschrieben ist. Sie entsteht
      // ausserhalb des Updaters, denn der muss rein bleiben - React ruft ihn im
      // StrictMode zweimal.
      const previous = docsRef.current.get(key)
      const vorlaeufig: LocalDoc = {
        kind: KIND.photo,
        id,
        body: {},
        rev: previous?.rev ?? 0,
        deleted: true,
        dirty: previous && previous.rev > 0 ? 1 : 0,
        updatedAt: now,
        localSeq: nextSeq(previous?.localSeq),
      }
      setDocs((current) => new Map(current).set(key, vorlaeufig))

      void (async () => {
        // Bewusst nicht der React-Zustand: läuft gerade ein Upload, steht die
        // vom Server vergebene Revision schon in der Datenbank, während der
        // Zustand noch die 0 zeigt. Wer sich darauf verlässt, löscht nur
        // lokal, und das Foto bleibt für immer auf dem Server.
        //
        // Lesen und Schreiben liegen in deletePhotoDoc in einer gemeinsamen
        // Transaktion über Dokumente und Bilddaten, spiegelbildlich zu
        // finishPhotoUpload. Getrennt passte der Abschluss eines Uploads genau
        // dazwischen: gelesene Revision 0, dann committet der Upload, dann
        // schriebe der Grabstein seine 0 und gälte als nicht schmutzig - er
        // ginge nie zum Server, und der nächste Pull holte das Foto zurück.
        const doc = await deletePhotoDoc(tripId, KIND.photo, id, now, nextSeq)
        if (stillgelegtRef.current) return
        setDocs((current) => new Map(current).set(key, doc))
        if (doc.dirty === 1) scheduleSync()
      })()
    },
    [tripId, darfSchreiben, nextSeq, scheduleSync],
  )

  const loadPhoto = useCallback(
    async (id: string, stageId: string) => {
      const engine = engineRef.current
      if (engine) return engine.loadPhoto(id, stageId)
      // Ohne laufenden Abgleich, also offline oder abgemeldet, bleibt nur der
      // lokale Bestand.
      return (await getBlob(tripId, id))?.blob ?? null
    },
    [tripId],
  )

  /**
   * Baut die Exportdatei stückweise.
   *
   * Der früher naheliegende Weg - alles in ein Objekt, ein JSON.stringify
   * darüber - kostet auf dem Telefon den Absturz. Ein Foto liegt seit der
   * Erhöhung auf 2400 Pixel bei rund 1,5 MB, als Base64 bei zwei. Bei sechzehn
   * Etappen mit je acht Bildern sind das gut 250 MB Text, und stringify braucht
   * daneben noch einmal so viel: eine einzelne Zeichenkette dieser Grösse ist
   * die Grenze, an der Browser mit "Invalid string length" abbrechen oder dem
   * Speicher ausgehen.
   *
   * Deshalb entsteht die Datei aus Abschnitten. Jedes Foto wandert einzeln in
   * einen eigenen Blob; danach ist seine Zeichenkette nicht mehr erreichbar und
   * der Speicher wieder frei. Was am Ende zusammengesetzt wird, sind Verweise auf
   * Blöcke, die der Browser ausserhalb des JavaScript-Speichers hält - nie
   * steht der ganze Text als eine Zeichenkette da.
   *
   * @param onFortschritt wird je verpacktem Foto gerufen. Bei hundert Bildern
   *   dauert das sichtbar lange, und die Oberfläche soll zeigen, dass etwas
   *   passiert.
   */
  const buildExport = useCallback(
    async (onFortschritt?: (fertig: number, gesamt: number) => void) => {
      // Die kleinen Abschnitte kommen weiterhin als Ganzes durch JSON.stringify:
      // sie sind zusammen einige hundert Kilobyte, und die Typprüfung gegen
      // ExportFile ist die Zusage, dass die Datei zum Import passt.
      const kopf: Omit<ExportFile, 'photos'> = {
        format: EXPORT_FORMAT,
        version: EXPORT_VERSION,
        exportedAt: new Date().toISOString(),
        trip,
        diary: Object.values(diary),
        // Die Dokumente und nicht die Anzeige: der noch nicht überführte alte
        // Tagebuchtext steht in postsByStage als Beitrag, gehört aber in keine
        // Datei - siehe beitraegeFuerExport in lib/diary.ts. Er reist trotzdem
        // mit, nämlich im Tagebucheintrag daneben.
        posts: beitraegeFuerExport(postDocs.nachEtappe, postDocs.bekannt),
        expenses,
        checks,
      }

      const teile: BlobPart[] = ['{\n']
      for (const [feld, wert] of Object.entries(kopf)) {
        teile.push(`  ${JSON.stringify(feld)}: ${eingerueckt(JSON.stringify(wert, null, 2))},\n`)
      }
      // Die Fotos stehen zuletzt, deshalb bleibt das Komma darüber gültig.
      teile.push('  "photos": [')

      const metas = Object.values(photosByStage).flat()
      onFortschritt?.(0, metas.length)

      let erstes = true
      for (const [i, meta] of metas.entries()) {
        const blob = await loadPhoto(meta.id, meta.stageId)
        if (blob) {
          const eintrag: TransferPhoto = {
            id: meta.id,
            stageId: meta.stageId,
            createdAt: meta.createdAt,
            dataUrl: await blobToDataUrl(blob),
          }
          // Sofort in einen Blob: nach dieser Zeile hält nichts mehr die Bild-
          // Zeichenkette, und der nächste Durchgang fängt wieder bei null an.
          teile.push(new Blob([erstes ? '\n    ' : ',\n    ', JSON.stringify(eintrag)]))
          erstes = false
        }
        onFortschritt?.(i + 1, metas.length)
      }

      teile.push(erstes ? ']\n}\n' : '\n  ]\n}\n')
      return new Blob(teile, { type: 'application/json' })
    },
    [trip, diary, postDocs, expenses, checks, photosByStage, loadPhoto],
  )

  /**
   * Schreibt den Inhalt einer Exportdatei in den lokalen Bestand. Steht für
   * sich, damit in importBundle nur die Sperre um den Abgleich übrig bleibt.
   */
  const schreibeImport = useCallback(
    async (bundle: Bundle) => {
      // Ein Import ersetzt den Bestand - den dieser Reise, und nur den. Alles
      // andere wäre ein Zusammenführen zweier Reisen, und das kann nur
      // schieflaufen.
      const now = Date.now()

      // Der Server weiss vom Import nichts. Was er kennt und das Bundle nicht
      // mitbringt, käme mit dem nächsten Pull zurück - genau die Vermischung,
      // die hier ausgeschlossen sein soll. Also erst den alten Bestand ansehen
      // und Grabsteine bilden, dann löschen: wipe() nimmt auch den Cursor mit,
      // und danach ist die Vorgeschichte nicht mehr zu ermitteln.
      const bestand = await allDocs(tripId)
      const tombstones = tombstonesForImport(
        bestand,
        {
          [KIND.diary]: new Set(bundle.diary.map((e) => e.stageId)),
          [KIND.post]: new Set(bundle.posts.map((p) => p.id)),
          [KIND.expense]: new Set(bundle.expenses.map((e) => e.id)),
          [KIND.photo]: new Set(bundle.photos.map((p) => p.id)),
        },
        now,
      )

      // Jedes importierte Dokument setzt auf der Revision auf, die der Server
      // bereits kennt. Ohne das schreibt der Import überall rev 0, und jeder
      // Push liefe in einen Konflikt, den erst der Zeitstempel entscheidet -
      // beim Import zwar zugunsten des Imports, aber nur über eine zusätzliche
      // Runde. Dieselbe Überlegung wie bei den Grabsteinen: nur mit der
      // richtigen Basis geht der Push auf Anhieb durch.
      const basis = new Map(bestand.map((d) => [docKey(d.kind, d.id), d.rev]))
      const revVon = (kind: string, id: string) => basis.get(docKey(kind, id)) ?? 0

      // Die Bilddaten der entfernten Fotos gehen im wipe() mit unter: es leert
      // den Blob-Speicher vollständig, und danach schreibt nur das Bundle
      // seine eigenen Bilder zurück. Andere Reisen fasst es nicht an - sie
      // liegen in eigenen Datenbanken.
      await wipe(tripId)

      const fresh: LocalDoc[] = [
        {
          kind: KIND.trip,
          id: SINGLETON_ID.trip,
          body: bundle.trip,
          rev: revVon(KIND.trip, SINGLETON_ID.trip),
          deleted: false,
          dirty: 1,
          updatedAt: now,
        },
        {
          kind: KIND.checks,
          id: SINGLETON_ID.checks,
          body: bundle.checks,
          rev: revVon(KIND.checks, SINGLETON_ID.checks),
          deleted: false,
          dirty: 1,
          updatedAt: now,
        },
      ]
      for (const entry of bundle.diary) {
        fresh.push({
          kind: KIND.diary,
          id: entry.stageId,
          body: entry,
          rev: revVon(KIND.diary, entry.stageId),
          deleted: false,
          dirty: 1,
          updatedAt: now,
        })
      }
      for (const post of bundle.posts) {
        fresh.push({
          kind: KIND.post,
          id: post.id,
          body: post,
          rev: revVon(KIND.post, post.id),
          deleted: false,
          dirty: 1,
          updatedAt: now,
        })
      }
      for (const expense of bundle.expenses) {
        fresh.push({
          kind: KIND.expense,
          id: expense.id,
          body: expense,
          rev: revVon(KIND.expense, expense.id),
          deleted: false,
          dirty: 1,
          updatedAt: now,
        })
      }
      for (const photo of bundle.photos) {
        const blob = await dataUrlToBlob(photo.dataUrl)
        // Verbraucht: die Bild-Zeichenkette wird hier freigegeben, sobald ihre
        // Bytes in der Datenbank liegen. Sonst hält das Bundle bis zum Ende des
        // Imports jedes Bild doppelt - als Text im Speicher und als Blob in der
        // Datenbank -, und genau daran geht auf dem Telefon der Import einer
        // grossen Datei zugrunde. Der Aufrufer darf das Bundle danach nicht noch
        // einmal importieren; er ruft ohnehin einmal und wirft es weg.
        photo.dataUrl = ''
        await putBlob(tripId, {
          id: photo.id,
          stageId: photo.stageId,
          blob,
          uploaded: 0,
          failed: 0,
          createdAt: photo.createdAt,
        })
        fresh.push({
          kind: KIND.photo,
          id: photo.id,
          body: {
            id: photo.id,
            stageId: photo.stageId,
            mime: blob.type || 'image/jpeg',
            size: blob.size,
            sha256: '',
            createdAt: photo.createdAt,
          } satisfies PhotoMeta,
          // Das Fotodokument entsteht endgültig beim Upload auf dem Server,
          // deshalb bleibt es hier unschmutzig. Die bekannte Revision steht
          // trotzdem drin, damit ein Pull vor dem Upload nichts durcheinander
          // bringt.
          rev: revVon(KIND.photo, photo.id),
          deleted: false,
          dirty: 0,
          updatedAt: now,
        })
      }

      // Die Grabsteine haben den wipe überlebt und gehen zusammen mit dem
      // Bundle in den Bestand. Sie sind schmutzig, der nächste Push räumt den
      // Server auf, bevor der Pull mit since=0 die alten Stücke zurückholt.
      const all = [...tombstones, ...fresh]
      await putDocs(tripId, all)
      setDocs(toMap(all))
    },
    [tripId],
  )

  const importBundle = useCallback(
    async (bundle: Bundle) => {
      const engine = engineRef.current
      try {
        // Erst den Abgleich stilllegen und den laufenden Durchgang abwarten.
        // Sonst leert wipe() die Datenbank unter einem offenen Push weg: dessen
        // Antwort findet ihre Dokumente nicht mehr und schriebe den alten Stand
        // unschmutzig zurück - über einzelne importierte Dokumente, die danach
        // nie wieder gepusht würden. Der Foto-Loop im Import hält dieses
        // Fenster mehrere Sekunden offen.
        //
        // Der Aufruf liegt mit im try: scheitert das Stilllegen, muss trotzdem
        // resume() laufen, sonst steht der Abgleich bis zum Neustart der App.
        await engine?.pause()
        await schreibeImport(bundle)
      } finally {
        // Auch nach einem Fehler mitten im Import muss der Abgleich
        // weiterlaufen, sonst steht er bis zum nächsten Start der App.
        engine?.resume()
      }
      void engine?.sync()
    },
    [schreibeImport],
  )

  /**
   * Stösst einen Abgleich an und reicht dessen Versprechen durch.
   *
   * Ohne Engine - nicht angemeldet, oder der Effekt oben hat sie gerade
   * abgebaut - kommt trotzdem ein erfülltes Versprechen zurück. Der Aufrufer
   * soll sich auf ein await verlassen können, ohne vorher zu wissen, ob
   * gerade abgeglichen werden kann.
   *
   * Ein Fehler bleibt hier und wird nicht weitergereicht. Zwei Gründe: er ist
   * im Statusobjekt schon angekommen, das die Statusleiste anzeigt, ein zweiter
   * Weg nach draussen brauchte es also nicht. Und der Aufruf ohne await bleibt
   * erlaubt - eine Ablehnung landete dann als unbehandelte Ablehnung in der
   * Konsole. Die Engine erfüllt ihr Versprechen zwar ohnehin immer (runOnce
   * fängt seine Fehler selbst ab), aber diese Zusage gehört an die Grenze
   * nach aussen und nicht in eine Annahme über die Innereien der Engine.
   */
  const syncNow = useCallback(async () => {
    try {
      await engineRef.current?.sync()
    } catch (err) {
      console.error('Abgleich fehlgeschlagen', err)
    }
  }, [])

  /**
   * Löscht den lokalen Bestand für das Abmelden.
   *
   * Der Weg über diese Funktion ist verbindlich: wipe() aus db.ts darf von
   * ausserhalb der Datenschicht nicht mehr direkt gerufen werden. Für sich
   * allein räumt es die Datenbank unter dem laufenden Abgleich weg, und der
   * liest danach den Cursor neu, findet die 0 und holt den gesamten Bestand des
   * eben abgemeldeten Nutzers vom Server zurück. Scheitert die Abmeldung beim
   * Anmeldedienst, bleibt er auf einem geteilten Gerät liegen - genau das, was
   * das Löschen verhindern soll.
   *
   * Also der Reihe nach: den Abgleich anhalten, den laufenden Durchgang
   * abwarten, dann erst löschen. Aufgenommen wird er danach nicht wieder; nach
   * dem Abmelden gibt es nichts mehr abzugleichen, und der Effekt oben baut die
   * Engine beim nächsten Anmelden ohnehin neu auf.
   *
   * Die Markierung fällt vor dem Warten, nicht danach: Schreibvorgänge, die
   * gerade unterwegs sind - vor allem das sekundenlange Verkleinern eines Fotos
   * - sollen sich beim Aufwachen verwerfen, statt in die geleerte Datenbank zu
   * schreiben.
   *
   * Und sie wird angezeigt. Im Normalfall baut die Anmeldeschicht den Provider
   * gleich darauf ab, dann sieht sie ohnehin niemand. Scheitert die Abmeldung
   * beim Anmeldedienst aber in beiden Anläufen (signoutRedirect und danach
   * removeUser, siehe AuthProvider.signOut), bleibt der Anmeldezustand auf
   * ready und die App steht weiter da - über einem geleerten Bestand, mit
   * einem Abgleich, der nicht mehr läuft, und einem write(), das ab hier alles
   * verwirft. Ohne diese Zeile sähe das aus wie eine App, die speichert.
   */
  const wipeForSignOut = useCallback(async () => {
    stillgelegtRef.current = true
    // Auch die Reiseliste, die Mitglieder und die Identität: sonst sähe der
    // Nächste am Gerät, mit wem der Vorgänger unterwegs war.
    vergissAlles()

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }

    const engine = engineRef.current
    engine?.stop()
    await engine?.pause()

    // Alle Reisen dieses Geräts, nicht nur die offene: nach dem Abmelden darf
    // auf einem geteilten Gerät nichts zurückbleiben, auch nicht die Reise,
    // in der der Nutzer gerade nicht war.
    await wipeAlles()
    setDocs(new Map())
    // Zuletzt und nicht zuerst: der Abgleich hängt mit seinem Listener noch am
    // Zustand, und ein Durchgang, der während des Stilllegens zu Ende geht,
    // meldet dabei sein eigenes Ergebnis - er schriebe die Anzeige sonst gleich
    // wieder auf "abgeglichen".
    setSync(statusNachStilllegung)
  }, [vergissAlles])

  const value = useMemo<RoadbookValue>(
    () => ({
      ready,
      tripId,
      darfSchreiben,
      trip,
      diary,
      postsByStage,
      expenses,
      checks,
      photosByStage,
      sync,
      syncNow,
      wipeForSignOut,
      setMeta,
      setVehicle,
      setStage,
      addStage,
      appendStages,
      deleteStage,
      shiftStage,
      setDiary,
      addPost,
      updatePost,
      deletePost,
      addExpense,
      deleteExpense,
      setChecks,
      addPhoto,
      deletePhoto,
      loadPhoto,
      buildExport,
      importBundle,
    }),
    [
      ready,
      tripId,
      darfSchreiben,
      trip,
      diary,
      postsByStage,
      expenses,
      checks,
      photosByStage,
      sync,
      syncNow,
      wipeForSignOut,
      setMeta,
      setVehicle,
      setStage,
      addStage,
      appendStages,
      deleteStage,
      shiftStage,
      setDiary,
      addPost,
      updatePost,
      deletePost,
      addExpense,
      deleteExpense,
      setChecks,
      addPhoto,
      deletePhoto,
      loadPhoto,
      buildExport,
      importBundle,
    ],
  )

  return <RoadbookContext.Provider value={value}>{children}</RoadbookContext.Provider>
}
